/**
 * DateRangePicker — preset chips + two date inputs returning a
 * { startAt, endAt } window in ISO datetime form (UTC) ready to
 * post to the reports endpoint.
 *
 * Conventions:
 *   - endAt is EXCLUSIVE — "Today" means [startOfToday, startOfTomorrow).
 *     The server's window filter follows the same convention so a
 *     30-day window includes exactly 30 days.
 *   - Day boundaries are UTC midnight at MVP scale (single-country
 *     deployment). Wall-clock per-country slices land when the
 *     country-config adds a timezone field.
 *   - "Custom" is the only mode that exposes the raw inputs; preset
 *     mode hides them so dealers don't accidentally pick a span that
 *     doesn't match their mental model of "this week".
 */
import { useEffect, useState } from "react";
import { cn } from "../lib/utils.js";

export interface DateRange {
  readonly startAt: string;
  readonly endAt: string;
  /** Bucket granularity inferred from the range length. */
  readonly bucket: "day" | "week" | "month";
  /** Human label for the current range, shown in the header. */
  readonly label: string;
}

export type DateRangePreset = "today" | "7d" | "30d" | "mtd" | "qtd" | "ytd" | "custom";

const PRESETS: ReadonlyArray<{ key: DateRangePreset; label: string }> = [
  { key: "today", label: "Today" },
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "mtd", label: "Month to date" },
  { key: "qtd", label: "Quarter to date" },
  { key: "ytd", label: "Year to date" },
  { key: "custom", label: "Custom" },
];

interface Props {
  readonly value: DateRange;
  readonly onChange: (next: DateRange) => void;
}

export function DateRangePicker({ value, onChange }: Props): JSX.Element {
  const [preset, setPreset] = useState<DateRangePreset>("30d");
  const [customStart, setCustomStart] = useState<string>(() => isoDate(new Date(value.startAt)));
  const [customEnd, setCustomEnd] = useState<string>(() => isoDate(new Date(value.endAt)));

  // Whenever the preset changes (non-custom), recompute + emit. We
  // intentionally omit onChange from the dep array — the parent
  // rebinds every render and would loop forever. The repo does not
  // run eslint-plugin-react-hooks so the suppression comment also
  // tripped (rule not found); a plain function reference plus this
  // comment keeps the intent documented without the disable directive.
  useEffect(() => {
    if (preset === "custom") return;
    onChange(rangeForPreset(preset));
  }, [preset]);

  // When in custom mode + the user edits a date input, emit the new range.
  function commitCustom(nextStart: string, nextEnd: string): void {
    setCustomStart(nextStart);
    setCustomEnd(nextEnd);
    if (!nextStart || !nextEnd) return;
    const start = new Date(nextStart);
    const end = new Date(nextEnd);
    // Push end to the END of the chosen day so the operator's
    // "12 Aug – 14 Aug" includes the 14th.
    end.setHours(23, 59, 59, 999);
    if (Number.isNaN(start.valueOf()) || Number.isNaN(end.valueOf())) return;
    if (end <= start) return;
    onChange({
      startAt: start.toISOString(),
      endAt: end.toISOString(),
      bucket: inferBucket(start, end),
      label: `${nextStart} – ${nextEnd}`,
    });
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => setPreset(p.key)}
            className={cn(
              "px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider transition-colors",
              preset === p.key
                ? "bg-simuflex-navy text-white"
                : "bg-white border border-simuflex-line text-simuflex-navy hover:bg-simuflex-mist",
            )}
          >
            {p.label}
          </button>
        ))}
      </div>
      {preset === "custom" ? (
        <div className="flex items-center gap-2 text-xs">
          <label className="flex items-center gap-1.5">
            <span className="font-bold text-simuflex-slate uppercase tracking-wider">From</span>
            <input
              type="date"
              value={customStart}
              onChange={(e) => commitCustom(e.target.value, customEnd)}
              className="rounded-lg border border-simuflex-line bg-white px-2 py-1 text-simuflex-navy"
            />
          </label>
          <label className="flex items-center gap-1.5">
            <span className="font-bold text-simuflex-slate uppercase tracking-wider">To</span>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => commitCustom(customStart, e.target.value)}
              className="rounded-lg border border-simuflex-line bg-white px-2 py-1 text-simuflex-navy"
            />
          </label>
        </div>
      ) : (
        <div className="text-[11px] text-simuflex-slate font-medium">{value.label}</div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 *  Pure helpers
 * ──────────────────────────────────────────────────────────────────── */

export function rangeForPreset(preset: DateRangePreset, now: Date = new Date()): DateRange {
  const end = new Date(now);
  // End-of-period is "now" so a mid-day refresh shows everything up
  // to the current minute. The server's `endAt` is exclusive; we
  // round the live minute to the wall-clock second so the UI feels
  // stable across re-renders.
  end.setSeconds(0, 0);
  const start = new Date(end);
  let label = "";
  switch (preset) {
    case "today": {
      start.setHours(0, 0, 0, 0);
      label = "Today";
      break;
    }
    case "7d": {
      start.setDate(start.getDate() - 7);
      start.setHours(0, 0, 0, 0);
      label = "Last 7 days";
      break;
    }
    case "30d": {
      start.setDate(start.getDate() - 30);
      start.setHours(0, 0, 0, 0);
      label = "Last 30 days";
      break;
    }
    case "mtd": {
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      label = "Month to date";
      break;
    }
    case "qtd": {
      const q = Math.floor(start.getMonth() / 3) * 3;
      start.setMonth(q, 1);
      start.setHours(0, 0, 0, 0);
      label = "Quarter to date";
      break;
    }
    case "ytd": {
      start.setMonth(0, 1);
      start.setHours(0, 0, 0, 0);
      label = "Year to date";
      break;
    }
    case "custom":
    default: {
      // Default custom == last 30 days; the picker UI takes over once
      // the user actually edits the dates.
      start.setDate(start.getDate() - 30);
      start.setHours(0, 0, 0, 0);
      label = "Custom";
    }
  }
  return {
    startAt: start.toISOString(),
    endAt: end.toISOString(),
    bucket: inferBucket(start, end),
    label,
  };
}

export function inferBucket(start: Date, end: Date): "day" | "week" | "month" {
  const days = (end.valueOf() - start.valueOf()) / (24 * 60 * 60 * 1000);
  if (days <= 60) return "day";
  if (days <= 365) return "week";
  return "month";
}

function isoDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
